import { Queuer } from '@tanstack/pacer';
import { useQuery } from '@tanstack/react-query';
import { useHatDbContext } from './context/HatDbContext';
import { HatMetricQueryWithPriority } from './HatMetrics';

type HatQueuedQuery = {
    query: HatMetricQueryWithPriority;
    authToken: string;
    baseUrl: string;
    signal: AbortSignal;
    resolve: Function;
    reject: Function;
};

const queue = new Queuer<HatQueuedQuery>(
    async (item) => {
        try {
            const response = await fetch(`${item.baseUrl}/cog/dbMetric`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': item.authToken,
                },
                body: JSON.stringify(item.query),
                signal: item.signal,
            });
            if (!response.ok) {
                item.reject('request failed');
                return;
            }
            item.resolve(await response.json());
        } catch (err) {
            console.log(err);
            item.reject('aborted');
        }
    },
    {
        wait: 100,
        getPriority: (item) => {
            return item.query.priority;
        },
    }
);

// Start processing
queue.start();

export function useHatQuery(query: HatMetricQueryWithPriority, enabled: boolean) {
    const hatContext = useHatDbContext();

    return useQuery({
        queryKey: [query],
        queryFn: ({ signal }) => {
            return new Promise((resolve, reject) => {
                queue.addItem({
                    signal,
                    authToken: hatContext.clickstreamConfig.authToken,
                    baseUrl: hatContext.clickstreamConfig.baseUrl,
                    query,
                    resolve,
                    reject,
                });
            });
        },
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 60, // 1 hour
        select: query.select,
        enabled: enabled && !!hatContext.clickstreamConfig.baseUrl && !!hatContext.clickstreamConfig.authToken,
    });
}
